import React, { createContext, useContext, useState, useEffect } from 'react';

interface Currency {
  rate: number;
  symbol: string;
  name: string;
  countries: string[];
}

interface Language {
  name: string;
  country: string;
  currency: string;
  flag: string;
}

interface GlobalProductsConfig {
  products: {
    [key: string]: {
      name: string;
      description: string;
      productId: string;
      prices: {
        [currency: string]: {
          priceId: string;
          amount: number;
          symbol: string;
          displayPrice: string;
        };
      };
    };
  };
  languages: {
    [key: string]: Language;
  };
  currencies: {
    [key: string]: Currency;
  };
}

interface GlobalContextType {
  currentLanguage: string;
  currentCurrency: string;
  currentCountry: string;
  config: GlobalProductsConfig | null;
  setLanguage: (lang: string) => void;
  getProductPrice: (productId: string) => any;
  formatPrice: (amount: number, currency?: string) => string;
}

const GlobalContext = createContext<GlobalContextType | undefined>(undefined);

export const GlobalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [config, setConfig] = useState<GlobalProductsConfig | null>(null);

  useEffect(() => {
    // Charger la configuration des produits globaux
    import('../config/global-products.json')
      .then((data) => setConfig(data.default))
      .catch((error) => console.error('Error loading products:', error));
  }, []);

  const setLanguage = (lang: string) => {
    setCurrentLanguage(lang);
    localStorage.setItem('komby-language', lang);
  };

  const currentCurrency = config?.languages[currentLanguage]?.currency || 'gbp';
  const currentCountry = config?.languages[currentLanguage]?.country || 'GB';

  const getProductPrice = (productId: string) => {
    if (!config) return null;
    return config.products[productId]?.prices[currentCurrency];
  };

  const formatPrice = (amount: number, currency?: string) => {
    const curr = currency || currentCurrency;
    const currencyInfo = config?.currencies[curr];
    if (!currencyInfo) return `${amount}`;
    
    const decimals = ['jpy', 'krw', 'vnd', 'idr'].includes(curr) ? 0 : 2;
    return `${currencyInfo.symbol}${(amount / 100).toFixed(decimals)}`;
  };

  return (
    <GlobalContext.Provider value={{
      currentLanguage,
      currentCurrency,
      currentCountry,
      config,
      setLanguage,
      getProductPrice,
      formatPrice
    }}>
      {children}
    </GlobalContext.Provider>
  );
};

export const useGlobal = () => {
  const context = useContext(GlobalContext);
  if (context === undefined) {
    throw new Error('useGlobal must be used within a GlobalProvider');
  }
  return context;
};
